import { IconAlertCircle, IconCheck } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { createRefusalKey, type ProjectCreateOutcome } from '../lib/project-create';
import { Button } from './ui/Button';

// What `POST /v1/repos` actually did (#1727). Presenter — and the SUCCESS is
// the one that needs the care, because there are two of them.
//
// A CREATED REPO IS A REAL SUCCESS: unlike a plan start, the 201 means the repo
// exists on GitHub and its row landed, so this wears a tick. What it must not
// claim is the SCAFFOLD — `scaffold_task_id` is null whenever the dispatch
// failed, and the repo is kept anyway with the reason audited, so that reading
// says the base was not established and points at the setup control that
// dispatches one (#1728). Inventing a session there would send a maintainer to
// the queue board to watch for a task nobody minted.
//
// A REFUSAL SPEAKS ON TWO CHANNELS, the `<StartPlanResult/>` shape: the framing
// line is chosen by status (`createRefusalKey`) and the API's own `detail`
// prints under it — including the 404, which on this route means the deployment
// holds no GitHub App credential rather than "no such repo".

export type CreateProjectResultProps = {
  readonly outcome: ProjectCreateOutcome;
  /** Back to an empty form, same modal. */
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

export function CreateProjectResult(props: CreateProjectResultProps): JSX.Element {
  // why the cast: `kind === K` narrows at runtime, but TS cannot carry that
  // narrowing through a generic type parameter (the `<StartPlanResult/>` idiom).
  const of = <K extends ProjectCreateOutcome['kind']>(
    kind: K,
  ): Extract<ProjectCreateOutcome, { kind: K }> | null =>
    props.outcome.kind === kind
      ? (props.outcome as Extract<ProjectCreateOutcome, { kind: K }>)
      : null;

  const repoHref = (fullName: string): string => orgHref(`/repos/${encodeURIComponent(fullName)}`);

  return (
    <>
      <Switch>
        <Match when={of('created')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconCheck />
              <p class="alert__title">
                {t('repos.create.result_created', { repo: o().full_name })}
              </p>
              <p class="alert__desc">
                <Show
                  when={o().scaffold_task_id}
                  fallback={t('repos.create.result_no_scaffold_desc')}
                >
                  {(task) => t('repos.create.result_scaffolding_desc', { task: task() })}
                </Show>
              </p>
              <A class="alert__action" href={repoHref(o().full_name)}>
                {t('repos.create.result_open_repo')}
              </A>
              <Show when={o().scaffold_task_id !== null}>
                <A class="alert__action" href={orgHref('/fleet')}>
                  {t('repos.create.result_open_queue')}
                </A>
              </Show>
            </div>
          )}
        </Match>

        {/* A 2xx that named no repository: it may have been minted, so neither
            "created" nor "try again" is honest — a retry on a name the caller
            now owns is exactly the collision the port refuses to cause. */}
        <Match when={of('unclear')}>
          <div class="alert alert--icon" role="alert">
            <IconAlertCircle />
            <p class="alert__title">{t('repos.create.result_unclear')}</p>
            <p class="alert__desc">{t('repos.create.result_unclear_desc')}</p>
          </div>
        </Match>

        <Match when={of('refused')}>
          {(o) => (
            <div class="alert alert--icon alert--destructive" role="alert">
              <IconAlertCircle />
              <p class="alert__title">{t(createRefusalKey(o().status))}</p>
              <Show when={o().detail}>{(detail) => <p class="alert__desc">{detail()}</p>}</Show>
            </div>
          )}
        </Match>
      </Switch>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onAgain}>
          {t('repos.create.another')}
        </Button>
        <Button onClick={props.onClose}>{t('repos.create.close')}</Button>
      </div>
    </>
  );
}
